import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatMoney, formatMoneyAxis, formatNumber } from '../shared/formatting';
import { BulletChart, RankedBar } from '../shared/charts';
import { BrandSpinner } from '../shared/BrandSpinner';
import { api } from '../shared/api';
import { DateInput } from '../shared/DateInput';
import { Card as UiCard, Btn, Pill, DataTable, Crumbs, Tabs, ui, type Col } from '../shared/ui';

export type PnlV2Bucket = {
    bucket_lvl1: string;
    kind: 'revenue' | 'cost' | 'memo';
    total_eur: number;
    count: number;
    by_period: Record<string, number>;
};

export type PnlV2Payload = {
    filters: {
        company_ids: number[];
        date_from: string;
        date_to: string;
        intercompany_mode: 'include' | 'eliminate' | 'isolate';
    };
    columns: { key: string; label: string }[];
    kpi: {
        revenue_eur: number;
        cogs_eur: number;
        opex_eur: number;
        financial_income_eur: number;
        financial_cost_eur: number;
        intercompany_eur: number;
        memo_eur: number;
        unclassified_eur: number;
        net_result_eur: number;
        ebitda_eur?: number;
    };
    buckets: PnlV2Bucket[];
    intercompany: null | {
        mode: string;
        total_revenue_eur: number;
        total_cost_eur: number;
        by_company_pair: Array<{ from_code: string; to_code: string; revenue_eur: number; cost_eur: number; lines: number }>;
    };
    targets_audit?: Array<{
        company_code: string;
        company_id: number;
        fiscal_year: number;
        net_facts: number;
        net_target: number | null;
        net_delta: number | null;
        net_delta_pct: number | null;
        revenue_facts: number;
        revenue_target: number | null;
        revenue_delta: number | null;
        source: string;
    }>;
    sub_buckets?: Array<{
        bucket_lvl1: string;
        bucket_lvl2: string;
        total_eur: number;
        count: number;
    }>;
    ratios?: Record<string, { label: string; label_en: string; value: number; fmt: 'pct' | 'money'; hint: string }>;
};

const BUCKET_COLORS: Record<string, string> = {
    'Prihodi': '#4caf50',
    'Financijski prihodi': '#26a69a',
    'COGS': '#e57373',
    'OPEX': '#ff9800',
    'Financijski troškovi': '#d32f2f',
    'Intercompany': '#9c27b0',
    'Memo': '#90a4ae',
    'Nerasporedeno': '#607d8b',
};

/**
 * Prevodi engleske Mapiranje LVL2 nazive (iz bi_pnl_mng_map / bridge) u hrvatski.
 * Locale='en' vraća original. Locale='hr' prevodi.
 */
const BUCKET_L2_HR: Record<string, string> = {
    'Fixed Salaries': 'Plaće (fiksne)',
    'Variable Salaries': 'Plaće (varijabilne)',
    'Salaries (Bonus)': 'Plaće (bonusi)',
    'Salaries': 'Plaće',
    'Advertising': 'Marketing i oglašavanje',
    'Rent for Store Network': 'Najam (poslovnice)',
    'Main office rent & utilities': 'Najam ureda + režije',
    'Avg Cost of Rent (Monthly)': 'Prosječni najam (mjesečno)',
    'Fixed utility & maintance costs': 'Režije i održavanje (fiksne)',
    'Variable utility & maintance costs': 'Režije i održavanje (varijabilne)',
    'IT services, software & servers': 'IT usluge, software, serveri',
    'LegaL & Accounting services & other costs': 'Pravne i knjigovodstvene usluge',
    'Legal & Accounting services & other costs': 'Pravne i knjigovodstvene usluge',
    'Car lease + fuel': 'Vozila (leasing + gorivo)',
    'Car Rent + Fuel - Management': 'Vozila (uprava)',
    'Travel Expenses': 'Putni troškovi',
    'Other incomes': 'Ostali prihodi',
    'Other expenses': 'Ostali rashodi',
    'Workshop': 'Radionica',
    'Backoffice': 'Backoffice',
    'SG&A': 'SG&A (ostali OPEX)',
    'COGS (1)': 'COGS (1) — direktni gold',
    'COGS (2)': 'COGS (2) — indirektni',
    'COGS (3)': 'COGS (3) — marketing',
    'Financial income': 'Financijski prihodi',
    'Financial expenses': 'Financijski troškovi',
    'REVENUE': 'Prihodi',
};

function translateBucket(label: string | null | undefined, locale: 'hr' | 'en'): string {
    if (!label) return '—';
    if (locale === 'en') return label;
    return BUCKET_L2_HR[label] ?? label;
}

export type ComparisonMode = 'none' | 'mom' | 'qoq' | 'yoy' | 'custom';

export type CustomComparePeriod = { date_from: string; date_to: string; label: string };

/** Helper: izračunaj date range od mode + period_key. */
export function periodRange(mode: ComparisonMode, key: string): { from: string; to: string } {
    if (mode === 'mom') {
        // key = "YYYY-MM"
        const [y, m] = key.split('-').map(Number);
        const from = `${y}-${String(m).padStart(2, '0')}-01`;
        const last = new Date(y, m, 0).getDate();
        const to = `${y}-${String(m).padStart(2, '0')}-${String(last).padStart(2, '0')}`;

        return { from, to };
    }
    if (mode === 'qoq') {
        // key = "YYYY-Q#"
        const [yy, qq] = key.split('-Q');
        const y = Number(yy);
        const q = Number(qq);
        const startM = (q - 1) * 3 + 1;
        const endM = q * 3;
        const lastDay = new Date(y, endM, 0).getDate();

        return { from: `${y}-${String(startM).padStart(2, '0')}-01`, to: `${y}-${String(endM).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}` };
    }
    if (mode === 'yoy') {
        return { from: `${key}-01-01`, to: `${key}-12-31` };
    }

    return { from: key, to: key };
}

/** Helper: lista perioda dostupnih u dropdown-u (zadnjih N period). */
export function periodOptions(mode: ComparisonMode, todayIso: string, count = 24): Array<{ key: string; label: string }> {
    const opts: Array<{ key: string; label: string }> = [];
    const today = new Date(todayIso);
    if (mode === 'mom') {
        for (let i = 0; i < count; i++) {
            const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            opts.push({ key, label: key });
        }
    } else if (mode === 'qoq') {
        for (let i = 0; i < count; i++) {
            const d = new Date(today.getFullYear(), today.getMonth() - i * 3, 1);
            const q = Math.floor(d.getMonth() / 3) + 1;
            const key = `${d.getFullYear()}-Q${q}`;
            if (!opts.find((o) => o.key === key)) opts.push({ key, label: key });
        }
    } else if (mode === 'yoy') {
        for (let i = 0; i < Math.min(count, 10); i++) {
            const y = today.getFullYear() - i;
            opts.push({ key: String(y), label: String(y) });
        }
    }

    return opts;
}

export type ExplicitCompare = {
    current_key: string;  // npr "2026-03" za MoM
    compare_key: string;  // npr "2026-02" za MoM
};

export function PnlV2Module({
    payload,
    comparePayload,
    loading,
    locale = 'hr',
    comparisonMode,
    onComparisonModeChange,
    intercompanyMode,
    onIntercompanyModeChange,
    customCompare,
    onCustomCompareChange,
    explicitCompare,
    onExplicitCompareChange,
    token,
    companyIds,
    dateFrom,
    dateTo,
    drillPath = '/pnl/v2/drilldown',
    drillExtra,
    toolbarExtra,
    belowKpi,
}: {
    payload: PnlV2Payload | null;
    comparePayload: PnlV2Payload | null;
    loading: boolean;
    locale?: 'hr' | 'en';
    comparisonMode: ComparisonMode;
    onComparisonModeChange: (m: ComparisonMode) => void;
    intercompanyMode: 'include' | 'eliminate' | 'isolate';
    onIntercompanyModeChange: (m: 'include' | 'eliminate' | 'isolate') => void;
    customCompare?: CustomComparePeriod;
    onCustomCompareChange?: (p: CustomComparePeriod) => void;
    explicitCompare?: ExplicitCompare;
    onExplicitCompareChange?: (p: ExplicitCompare) => void;
    token?: string | null;
    companyIds?: number[];
    dateFrom?: string;
    dateTo?: string;
    /** m.2.3: drill ide na drugi endpoint (npr. /pl-v23/drilldown) s dodatnim parametrima (nacin, sloj). */
    drillPath?: string;
    drillExtra?: Record<string, string>;
    /** m.2.3: dodatni preklopnici u traci alata i blok ispod KPI kartica (slojevi). */
    toolbarExtra?: React.ReactNode;
    belowKpi?: React.ReactNode;
}) {
    const [drilldown, setDrilldown] = useState<{ lvl1: string; lvl2?: string; lvl3?: string; sub_buckets: any[]; suppliers: any[]; invoices: any[] } | null>(null);
    const [drilldownLoading, setDrilldownLoading] = useState(false);

    const openDrilldown = useCallback(async (lvl1: string, lvl2?: string, lvl3?: string) => {
        if (!token || !companyIds || !dateFrom || !dateTo) return;
        setDrilldownLoading(true);
        const params = new URLSearchParams({
            company_ids: companyIds.join(','),
            date_from: dateFrom,
            date_to: dateTo,
            bucket_lvl1: lvl1,
        });
        if (lvl2) params.set('bucket_lvl2', lvl2);
        if (lvl3) params.set('bucket_lvl3', lvl3);
        for (const [k, v] of Object.entries(drillExtra ?? {})) params.set(k, v);
        try {
            const resp = await api<{ sub_buckets: any[]; suppliers: any[]; invoices: any[] }>(`${drillPath}?${params.toString()}`, token);
            setDrilldown({ lvl1, lvl2, lvl3, sub_buckets: resp.sub_buckets ?? [], suppliers: resp.suppliers ?? [], invoices: resp.invoices ?? [] });
        } catch (e) {
            console.error(e);
        } finally {
            setDrilldownLoading(false);
        }
    }, [token, companyIds, dateFrom, dateTo, drillPath, drillExtra]);

    const kpiCards = useMemo(() => {
        if (!payload) return [];
        const k = payload.kpi;
        const gross = k.revenue_eur + k.cogs_eur; // cogs is negative
        const grossPct = k.revenue_eur > 0 ? (gross / k.revenue_eur) * 100 : 0;
        const opPct = k.revenue_eur > 0 ? (k.net_result_eur / k.revenue_eur) * 100 : 0;
        const cmpK = comparePayload?.kpi;

        function delta(curr: number, prev: number | undefined): { abs: number; pct: number } | null {
            if (prev === undefined || prev === 0) return null;
            const abs = curr - prev;
            const pct = (abs / Math.abs(prev)) * 100;
            return { abs, pct };
        }

        return [
            { label: locale === 'en' ? 'Revenue' : 'Prihodi', value: k.revenue_eur, color: BUCKET_COLORS['Prihodi'], delta: delta(k.revenue_eur, cmpK?.revenue_eur) },
            { label: locale === 'en' ? 'COGS' : 'COGS', value: k.cogs_eur, color: BUCKET_COLORS['COGS'], delta: delta(k.cogs_eur, cmpK?.cogs_eur) },
            { label: locale === 'en' ? 'Gross margin' : 'Bruto marža', value: gross, color: '#388e3c', pct: grossPct, delta: delta(gross, cmpK ? cmpK.revenue_eur + cmpK.cogs_eur : undefined) },
            { label: locale === 'en' ? 'OPEX' : 'OPEX', value: k.opex_eur, color: BUCKET_COLORS['OPEX'], delta: delta(k.opex_eur, cmpK?.opex_eur) },
            { label: locale === 'en' ? 'Financial' : 'Financ. net', value: k.financial_income_eur + k.financial_cost_eur, color: '#1976d2', delta: delta(k.financial_income_eur + k.financial_cost_eur, cmpK ? (cmpK.financial_income_eur + cmpK.financial_cost_eur) : undefined) },
            ...(k.ebitda_eur !== undefined ? [{ label: 'EBITDA', value: k.ebitda_eur, color: k.ebitda_eur >= 0 ? '#2e7d32' : '#c62828', pct: k.revenue_eur > 0 ? (k.ebitda_eur / k.revenue_eur) * 100 : 0, delta: delta(k.ebitda_eur, cmpK?.ebitda_eur) }] : []),
            { label: locale === 'en' ? 'Net result' : 'Neto rezultat', value: k.net_result_eur, color: k.net_result_eur >= 0 ? '#2e7d32' : '#c62828', pct: opPct, hero: true, delta: delta(k.net_result_eur, cmpK?.net_result_eur) },
        ];
    }, [payload, comparePayload, locale]);

    const monthlyChart = useMemo(() => {
        if (!payload) return null;
        const months = payload.columns.map((c) => c.key);
        // Stacked: revenue stack (positive) + cost stack (negative)
        const series: Array<{ bucket: string; color: string; values: number[]; kind: string }> = [];
        for (const b of payload.buckets) {
            const values = months.map((m) => b.by_period[m] ?? 0);
            if (values.every((v) => Math.abs(v) < 0.01)) continue;
            series.push({ bucket: b.bucket_lvl1, color: BUCKET_COLORS[b.bucket_lvl1] ?? '#999', values, kind: b.kind });
        }

        return { months, series };
    }, [payload]);

    const isHrms = typeof document !== 'undefined' && document.body.dataset.theme === 'hrms';

    if (loading && !payload) {
        return (
            <div className="card" style={{ minHeight: 320, display: 'grid', placeItems: 'center' }}>
                <BrandSpinner size="lg" label={locale === 'en' ? 'Loading P&L v2…' : 'Učitavam P&L v2…'} />
            </div>
        );
    }
    if (!payload) {
        return <div className="card">{locale === 'en' ? 'No data' : 'Nema podataka'}</div>;
    }

    // ── HRMS (moderni) — kit chrome: Crumbs + kit Cards/Tabs/Pills/DataTable. ──
    // Sva data/handleri identični legacy putanji; mijenja se samo vizualni omotač.
    if (isHrms) {
        // Binarno (Vedran 29.09.2026): konsolidacija ili ima ili nema. 'isolate' se vise ne nudi u UI-u;
        // ostaje samo interno (icAudit forsira isolate u PlV23Controller) i u tipu radi kompatibilnosti.
        const icTabs = ([
            { key: 'include', label: locale === 'en' ? 'Per-company' : 'Per-firma' },
            { key: 'eliminate', label: locale === 'en' ? 'Group consolidated' : 'Konsolidirano' },
        ] as Array<{ key: 'include' | 'eliminate' | 'isolate'; label: string }>);
        const cmpTabs = ([
            { key: 'none', label: '—' }, { key: 'mom', label: 'MoM' }, { key: 'qoq', label: 'QoQ' },
            { key: 'yoy', label: 'YoY' }, { key: 'custom', label: locale === 'en' ? 'Custom' : 'Vlastito' },
        ] as Array<{ key: ComparisonMode; label: string }>);

        type BucketRow = PnlV2Bucket & { id: string };
        const bucketRows: BucketRow[] = payload.buckets.map((b) => ({ ...b, id: b.bucket_lvl1 }));
        const bucketCols: Col<BucketRow>[] = [
            {
                key: 'bucket', label: locale === 'en' ? 'Bucket' : 'Kategorija',
                render: (b) => {
                    const isOpen = drilldown?.lvl1 === b.bucket_lvl1;
                    return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 700, color: ui.ink }}>
                        <span style={{ width: 10, height: 10, borderRadius: 3, background: BUCKET_COLORS[b.bucket_lvl1] ?? '#999' }} />
                        {isOpen ? '▾' : '▸'} {translateBucket(b.bucket_lvl1, locale)}
                    </span>;
                },
            },
            { key: 'total', label: locale === 'en' ? 'Total' : 'Ukupno', align: 'right', render: (b) => <span style={{ fontWeight: 700, color: b.total_eur >= 0 ? '#1a7a40' : '#c0264a' }}>{formatMoney(b.total_eur, locale)}</span> },
            { key: 'count', label: locale === 'en' ? 'Lines' : 'Redaka', align: 'right', render: (b) => <span style={{ color: ui.ink2 }}>{formatNumber(b.count, locale)}</span> },
            { key: 'pct', label: '%', align: 'right', render: (b) => <span style={{ color: ui.ink2 }}>{formatNumber(payload.kpi.revenue_eur > 0 ? (b.total_eur / payload.kpi.revenue_eur) * 100 : 0, locale)}%</span> },
        ];

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <Crumbs items={[locale === 'en' ? 'Dashboard' : 'Dashboard', 'P&L']} />

                {/* TOOLBAR — IC mode + comparison */}
                <UiCard pad={14} style={{ display: 'flex', flexWrap: 'wrap', gap: 18, alignItems: 'flex-end' }}>
                    <div>
                        <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6, color: ui.muted, fontWeight: 700 }}>Intercompany</div>
                        <Tabs size="sm" tabs={icTabs} active={intercompanyMode} onChange={(k) => onIntercompanyModeChange(k)} />
                    </div>
                    <div>
                        <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6, color: ui.muted, fontWeight: 700 }}>{locale === 'en' ? 'Compare to' : 'Usporedi s'}</div>
                        <Tabs size="sm" tabs={cmpTabs} active={comparisonMode} onChange={(k) => onComparisonModeChange(k)} />
                    </div>
                    {(comparisonMode === 'mom' || comparisonMode === 'qoq' || comparisonMode === 'yoy') && explicitCompare && onExplicitCompareChange ? (() => {
                        const opts = periodOptions(comparisonMode, new Date().toISOString().slice(0, 10), comparisonMode === 'yoy' ? 10 : 24);
                        const curRange = periodRange(comparisonMode, explicitCompare.current_key);
                        const cmpRange = periodRange(comparisonMode, explicitCompare.compare_key);
                        const periodLabel = comparisonMode === 'mom' ? (locale === 'en' ? 'Month' : 'Mjesec') : comparisonMode === 'qoq' ? (locale === 'en' ? 'Quarter' : 'Kvartal') : (locale === 'en' ? 'Year' : 'Godina');
                        const sel: React.CSSProperties = { height: 38, padding: '0 10px', borderRadius: ui.radiusSm, border: `1px solid ${ui.line}`, fontSize: '0.85rem', color: ui.ink, minWidth: 120 };
                        return (
                            <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                <div>
                                    <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6, color: ui.muted, fontWeight: 700 }}>{locale === 'en' ? `Current ${periodLabel}` : `Trenutni ${periodLabel}`}</div>
                                    <select value={explicitCompare.current_key} onChange={(e) => onExplicitCompareChange({ ...explicitCompare, current_key: e.target.value })} style={sel}>
                                        {opts.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                                    </select>
                                    <div style={{ fontSize: '0.7rem', color: ui.muted, marginTop: 4 }}>{curRange.from} → {curRange.to}</div>
                                </div>
                                <div style={{ fontSize: '1.2rem', color: ui.muted, paddingBottom: 10 }}>vs</div>
                                <div>
                                    <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6, color: ui.muted, fontWeight: 700 }}>{locale === 'en' ? `Compare ${periodLabel}` : `Usporedi ${periodLabel}`}</div>
                                    <select value={explicitCompare.compare_key} onChange={(e) => onExplicitCompareChange({ ...explicitCompare, compare_key: e.target.value })} style={sel}>
                                        {opts.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                                    </select>
                                    <div style={{ fontSize: '0.7rem', color: ui.muted, marginTop: 4 }}>{cmpRange.from} → {cmpRange.to}</div>
                                </div>
                            </div>
                        );
                    })() : null}
                    {comparisonMode === 'custom' && customCompare && onCustomCompareChange ? (
                        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
                            <label style={{ fontSize: '0.78rem', color: ui.ink2 }}>
                                {locale === 'en' ? 'Compare from' : 'Usporedba od'}
                                <DateInput value={customCompare.date_from} onChange={(iso) => onCustomCompareChange({ ...customCompare, date_from: iso })} style={{ display: 'block', marginTop: 4 }} />
                            </label>
                            <label style={{ fontSize: '0.78rem', color: ui.ink2 }}>
                                {locale === 'en' ? 'Compare to' : 'Usporedba do'}
                                <DateInput value={customCompare.date_to} onChange={(iso) => onCustomCompareChange({ ...customCompare, date_to: iso })} style={{ display: 'block', marginTop: 4 }} />
                            </label>
                        </div>
                    ) : null}
                    {toolbarExtra}
                </UiCard>

                {/* KPI CARDS */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12 }}>
                    {kpiCards.map((k) => (
                        <UiCard key={k.label} pad={16} style={{ borderLeft: `4px solid ${k.color}` }}>
                            <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: ui.muted, fontWeight: 700, marginBottom: 6 }}>{k.label}</div>
                            <div style={{ fontSize: k.hero ? '1.6rem' : '1.25rem', fontWeight: 800, color: k.value >= 0 ? ui.ink : '#c0264a' }}>{formatMoney(k.value, locale)}</div>
                            {k.pct !== undefined ? <div style={{ fontSize: '0.78rem', color: ui.ink2, marginTop: 3 }}>{formatNumber(k.pct, locale)}%</div> : null}
                            {k.delta ? (
                                <div style={{ fontSize: '0.78rem', marginTop: 5, fontWeight: 700, color: k.delta.pct >= 0 ? '#1a7a40' : '#c0264a' }}>
                                    {k.delta.pct >= 0 ? '▲' : '▼'} {formatNumber(Math.abs(k.delta.pct), locale)}% ({k.delta.abs >= 0 ? '+' : ''}{formatMoney(k.delta.abs, locale)})
                                </div>
                            ) : null}
                        </UiCard>
                    ))}
                </div>
                {belowKpi}

                {/* RATIO KPI GRID */}
                {payload.ratios ? (
                    <UiCard pad={18}>
                        <div style={{ fontSize: '1.02rem', fontWeight: 800, color: ui.ink, marginBottom: 12 }}>{locale === 'en' ? 'Financial ratios' : 'Financijski pokazatelji'}</div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 14 }}>
                            {Object.entries(payload.ratios).map(([key, r]) => {
                                const cmpR = comparePayload?.ratios?.[key];
                                const delta = cmpR ? r.value - cmpR.value : null;
                                const label = locale === 'en' ? r.label_en : r.label;
                                const isMoney = r.fmt === 'money';
                                return (
                                    <div key={key} style={{ padding: '10px 12px', border: `1px solid ${ui.line}`, borderRadius: ui.radiusSm, background: '#fafbfd' }}>
                                        <div style={{ fontSize: '0.7rem', color: ui.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3, fontWeight: 700 }}>{label}</div>
                                        <div style={{ fontSize: '1.1rem', fontWeight: 800, color: ui.ink }}>{isMoney ? formatMoney(r.value, locale) : formatNumber(r.value, locale) + '%'}</div>
                                        {delta !== null ? (
                                            <div style={{ fontSize: '0.72rem', marginTop: 3, fontWeight: 700, color: delta >= 0 ? '#1a7a40' : '#c0264a' }}>
                                                {delta >= 0 ? '▲' : '▼'} {isMoney ? formatMoney(Math.abs(delta), locale) : formatNumber(Math.abs(delta), locale) + 'pp'}
                                            </div>
                                        ) : null}
                                        <div style={{ fontSize: '0.66rem', color: ui.muted, marginTop: 4 }}>{r.hint}</div>
                                    </div>
                                );
                            })}
                        </div>
                        {(() => {
                            const pctRatios = Object.entries(payload.ratios!).filter(([, r]) => r.fmt === 'pct').slice(0, 8);
                            if (pctRatios.length === 0) return null;
                            return (
                                <div>
                                    <div style={{ fontSize: '0.72rem', margin: '0 0 6px', letterSpacing: '0.1em', textTransform: 'uppercase', color: ui.muted, fontWeight: 700 }}>
                                        {locale === 'en' ? 'Bullet view (actual ↔ prior)' : 'Bullet pogled (sada ↔ prošli period)'}
                                    </div>
                                    <div style={{ display: 'grid', gap: 6 }}>
                                        {pctRatios.map(([key, r]) => {
                                            const cmp = comparePayload?.ratios?.[key];
                                            const target = cmp?.value;
                                            const candidates = [Math.abs(r.value), Math.abs(target ?? 0), 10];
                                            const max = Math.max(Math.max(...candidates), Math.abs(r.value) * 1.4);
                                            const label = locale === 'en' ? r.label_en : r.label;
                                            return (
                                                <BulletChart key={key} label={label} actual={r.value} target={target} max={max} locale={locale}
                                                    valueFormatter={(n) => formatNumber(n, locale) + '%'}
                                                    zones={[{ until: max * 0.33, color: '#fce4e4' }, { until: max * 0.66, color: '#fff4d6' }, { until: max, color: '#e3f5e8' }]} />
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })()}
                    </UiCard>
                ) : null}

                {/* NORMALIZED COMPARISON */}
                {comparePayload && payload ? (
                    <UiCard pad={18}>
                        <div style={{ fontSize: '1.02rem', fontWeight: 800, color: ui.ink, marginBottom: 12 }}>{locale === 'en' ? 'Normalized comparison (base = 100)' : 'Normalizirana usporedba (baza = 100)'}</div>
                        <NormalizedComparisonChart current={payload} compare={comparePayload} locale={locale} />
                    </UiCard>
                ) : null}

                {/* MONTHLY STACKED BAR */}
                {monthlyChart && monthlyChart.months.length > 0 ? (
                    <UiCard pad={18}>
                        <div style={{ fontSize: '1.02rem', fontWeight: 800, color: ui.ink, marginBottom: 12 }}>{locale === 'en' ? 'Monthly P&L composition' : 'Mjesečni sastav P&L'}</div>
                        <StackedBarChart series={monthlyChart.series} months={monthlyChart.months} locale={locale} />
                        <Legend buckets={monthlyChart.series.map((s) => ({ bucket: s.bucket, color: s.color }))} locale={locale} />
                    </UiCard>
                ) : null}

                {/* BUCKETS + inline drilldown */}
                <UiCard pad={0}>
                    <div style={{ fontSize: '1.02rem', fontWeight: 800, color: ui.ink, padding: '16px 18px 8px' }}>{locale === 'en' ? 'Buckets' : 'Kategorije'}</div>
                    <DataTable<BucketRow> cols={bucketCols} rows={bucketRows}
                        onRowClick={(b) => (drilldown?.lvl1 === b.bucket_lvl1 ? setDrilldown(null) : openDrilldown(b.bucket_lvl1))} />
                    {drilldown ? (
                        <div style={{ margin: '0 18px 18px', padding: '14px 16px', borderLeft: `4px solid ${ui.acc}`, background: ui.accSoft, borderRadius: ui.radiusSm }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                <div style={{ fontWeight: 800, color: ui.ink }}>
                                    🔍 Drilldown: {translateBucket(drilldown.lvl1, locale)}
                                    {drilldown.lvl2 ? <span style={{ color: ui.muted }}> / {translateBucket(drilldown.lvl2, locale)}</span> : null}
                                    {drilldown.lvl3 ? <span style={{ color: ui.muted }}> / {drilldown.lvl3}</span> : null}
                                </div>
                                <Btn size="sm" onClick={() => setDrilldown(null)}>✕ {locale === 'en' ? 'Close' : 'Zatvori'}</Btn>
                            </div>
                            {drilldownLoading ? <BrandSpinner size="sm" label={locale === 'en' ? 'Loading…' : 'Učitavam…'} /> : null}
                            {!drilldown.lvl2 ? (
                                <div>
                                    <div style={{ fontWeight: 700, color: ui.ink2, margin: '6px 0' }}>{locale === 'en' ? 'Sub-categories (Mapiranje LVL2)' : 'Sub-kategorije (Mapiranje LVL2)'}</div>
                                    {drilldown.sub_buckets.length > 0 ? (
                                        <div style={{ background: '#fafbfd', border: `1px solid ${ui.line}`, borderRadius: ui.radiusSm, padding: '10px 12px', marginBottom: 12 }}>
                                            <RankedBar locale={locale} maxBars={10}
                                                items={drilldown.sub_buckets.map((s: any) => ({ label: translateBucket(s.bucket_lvl2, locale), value: s.total_eur, sub: `${formatNumber(s.count, locale)} ${locale === 'en' ? 'lines' : 'redaka'}` }))}
                                                moneyFormatter={(n) => formatMoney(n, locale)} />
                                        </div>
                                    ) : null}
                                    <DataTable
                                        cols={[
                                            { key: 'sub', label: locale === 'en' ? 'Sub-category' : 'Sub-kategorija', render: (s: any) => <span>▸ <strong>{translateBucket(s.bucket_lvl2, locale)}</strong></span> },
                                            { key: 'total', label: locale === 'en' ? 'Total' : 'Ukupno', align: 'right', render: (s: any) => <span style={{ fontWeight: 700, color: s.total_eur >= 0 ? '#1a7a40' : '#c0264a' }}>{formatMoney(s.total_eur, locale)}</span> },
                                            { key: 'count', label: locale === 'en' ? 'Lines' : 'Redaka', align: 'right', render: (s: any) => formatNumber(s.count, locale) },
                                        ] as Col<any>[]}
                                        rows={drilldown.sub_buckets.map((s: any, i: number) => ({ ...s, id: `${s.bucket_lvl2}-${i}` }))}
                                        onRowClick={(s: any) => openDrilldown(drilldown.lvl1, s.bucket_lvl2)} />
                                </div>
                            ) : !drilldown.lvl3 ? (
                                <div>
                                    <Btn size="sm" variant="soft" onClick={() => openDrilldown(drilldown.lvl1)}>← {locale === 'en' ? 'Back to sub-categories' : 'Natrag na sub-kategorije'}</Btn>
                                    <div style={{ fontWeight: 700, color: ui.ink2, margin: '10px 0 6px' }}>{locale === 'en' ? 'Top suppliers / partners' : 'Top dobavljači / partneri'} <span style={{ color: ui.muted, fontWeight: 400 }}>({translateBucket(drilldown.lvl2, locale)})</span></div>
                                    <DataTable
                                        cols={[
                                            { key: 'partner', label: locale === 'en' ? 'Partner / Subject' : 'Partner / Subjekt', render: (s: any) => <span>▸ <strong>{s.bucket_lvl3 || s.partner_subjekt}</strong></span> },
                                            { key: 'total', label: locale === 'en' ? 'Total' : 'Ukupno', align: 'right', render: (s: any) => <span style={{ fontWeight: 700, color: s.total_eur >= 0 ? '#1a7a40' : '#c0264a' }}>{formatMoney(s.total_eur, locale)}</span> },
                                            { key: 'count', label: locale === 'en' ? 'Lines' : 'Redaka', align: 'right', render: (s: any) => formatNumber(s.count, locale) },
                                        ] as Col<any>[]}
                                        rows={drilldown.suppliers.map((s: any, i: number) => ({ ...s, id: `${s.bucket_lvl3 || s.partner_subjekt}-${i}` }))}
                                        onRowClick={(s: any) => openDrilldown(drilldown.lvl1, drilldown.lvl2, s.bucket_lvl3)} />
                                </div>
                            ) : (
                                <div>
                                    <Btn size="sm" variant="soft" onClick={() => openDrilldown(drilldown.lvl1, drilldown.lvl2)}>← {locale === 'en' ? 'Back to suppliers' : 'Natrag na dobavljače'}</Btn>
                                    <div style={{ fontWeight: 700, color: ui.ink2, margin: '10px 0 6px' }}>{locale === 'en' ? 'Top invoices / documents' : 'Top fakture / dokumenti'} <span style={{ color: ui.muted, fontWeight: 400 }}>({drilldown.lvl3})</span></div>
                                    <DataTable
                                        cols={[
                                            { key: 'document', label: locale === 'en' ? 'Document' : 'Dokument', render: (inv: any) => inv.document },
                                            { key: 'datum', label: locale === 'en' ? 'Date' : 'Datum', render: (inv: any) => inv.datum },
                                            { key: 'konto', label: 'Konto', render: (inv: any) => inv.konto },
                                            { key: 'naziv', label: locale === 'en' ? 'Account name' : 'Naziv konta', render: (inv: any) => <span style={{ fontSize: '0.78rem', color: ui.ink2 }}>{inv.naziv_konta}</span> },
                                            { key: 'amount', label: locale === 'en' ? 'Amount' : 'Iznos', align: 'right', render: (inv: any) => <span style={{ fontWeight: 700, color: inv.amount_eur >= 0 ? '#1a7a40' : '#c0264a' }}>{formatMoney(inv.amount_eur, locale)}</span> },
                                        ] as Col<any>[]}
                                        rows={drilldown.invoices.map((inv: any, i: number) => ({ ...inv, id: i }))} />
                                </div>
                            )}
                        </div>
                    ) : null}
                </UiCard>

                {/* TARGETS AUDIT */}
                {payload.targets_audit && payload.targets_audit.length > 0 ? (
                    <UiCard pad={0}>
                        <div style={{ fontSize: '1.02rem', fontWeight: 800, color: ui.ink, padding: '16px 18px 8px' }}>{locale === 'en' ? 'Targets audit (facts vs GFI Tekuća)' : 'Targets audit (facts vs GFI Tekuća)'}</div>
                        <DataTable
                            cols={[
                                { key: 'company', label: locale === 'en' ? 'Company' : 'Firma', render: (t: any) => <strong>{t.company_code}</strong> },
                                { key: 'net_facts', label: 'Net facts', align: 'right', render: (t: any) => formatMoney(t.net_facts, locale) },
                                { key: 'net_gfi', label: 'Net GFI', align: 'right', render: (t: any) => t.net_target !== null ? formatMoney(t.net_target, locale) : '—' },
                                { key: 'delta_eur', label: 'Δ EUR', align: 'right', render: (t: any) => t.net_delta !== null ? formatMoney(t.net_delta, locale) : '—' },
                                { key: 'delta_pct', label: 'Δ %', align: 'right', render: (t: any) => t.net_delta_pct !== null ? formatNumber(t.net_delta_pct, locale) + '%' : '—' },
                                {
                                    key: 'status', label: 'Status', align: 'right', render: (t: any) => {
                                        const ok = t.net_target !== null && Math.abs(t.net_delta ?? 0) < 100;
                                        const warn = t.net_target !== null && !ok && Math.abs(t.net_delta_pct ?? 0) < 1;
                                        if (t.net_target === null) return <Pill tone="neutral">—</Pill>;
                                        return <Pill tone={ok ? 'ok' : warn ? 'warn' : 'bad'}>{ok ? '✓' : warn ? '⚠ <1%' : '❌ >1%'}</Pill>;
                                    },
                                },
                            ] as Col<any>[]}
                            rows={payload.targets_audit.map((t) => ({ ...t, id: t.company_code }))} />
                        <div style={{ padding: '8px 18px 16px', fontSize: '0.78rem', color: ui.muted }}>
                            {locale === 'en' ? 'Targets sourced from GFI POD (AOP 186 Tekuća). Edit in `bi_pnl_targets`.' : 'Targets iz GFI POD (AOP 186 Tekuća). Edit u tablici `bi_pnl_targets`.'}
                        </div>
                    </UiCard>
                ) : null}

                {/* SUB-BUCKETS DETAIL */}
                {payload.sub_buckets && payload.sub_buckets.length > 0 ? (
                    <UiCard pad={0}>
                        <div style={{ fontSize: '1.02rem', fontWeight: 800, color: ui.ink, padding: '16px 18px 8px' }}>{locale === 'en' ? 'Sub-bucket detail' : 'Detalj po sub-kategoriji'}</div>
                        <DataTable
                            cols={[
                                { key: 'bucket', label: locale === 'en' ? 'Bucket' : 'Kategorija', render: (s: any) => translateBucket(s.bucket_lvl1, locale) },
                                { key: 'sub', label: locale === 'en' ? 'Sub-bucket' : 'Sub-kategorija', render: (s: any) => <span>▸ {translateBucket(s.bucket_lvl2, locale)}</span> },
                                { key: 'total', label: locale === 'en' ? 'Total' : 'Ukupno', align: 'right', render: (s: any) => <span style={{ fontWeight: 700, color: s.total_eur >= 0 ? '#1a7a40' : '#c0264a' }}>{formatMoney(s.total_eur, locale)}</span> },
                                { key: 'pct', label: '%', align: 'right', render: (s: any) => formatNumber(payload.kpi.revenue_eur > 0 ? (s.total_eur / payload.kpi.revenue_eur) * 100 : 0, locale) + '%' },
                                { key: 'count', label: locale === 'en' ? 'Lines' : 'Redaka', align: 'right', render: (s: any) => formatNumber(s.count, locale) },
                            ] as Col<any>[]}
                            rows={payload.sub_buckets.slice(0, 30).map((s, i) => ({ ...s, id: `${s.bucket_lvl1}-${s.bucket_lvl2}-${i}` }))}
                            onRowClick={(s: any) => openDrilldown(s.bucket_lvl1, s.bucket_lvl2)} />
                    </UiCard>
                ) : null}

                {/* INTERCOMPANY AUDIT */}
                {payload.intercompany && payload.intercompany.by_company_pair.length > 0 ? (
                    <UiCard pad={0}>
                        <div style={{ fontSize: '1.02rem', fontWeight: 800, color: ui.ink, padding: '16px 18px 8px' }}>{locale === 'en' ? 'Intercompany pairs' : 'Intercompany parovi'} ({payload.intercompany.mode})</div>
                        <DataTable
                            cols={[
                                { key: 'from', label: locale === 'en' ? 'From' : 'Od', render: (p: any) => p.from_code },
                                { key: 'to', label: locale === 'en' ? 'To' : 'Prema', render: (p: any) => p.to_code },
                                { key: 'revenue', label: locale === 'en' ? 'Revenue' : 'Prihod', align: 'right', render: (p: any) => <span style={{ color: '#1a7a40', fontWeight: 700 }}>{formatMoney(p.revenue_eur, locale)}</span> },
                                { key: 'cost', label: locale === 'en' ? 'Cost' : 'Trošak', align: 'right', render: (p: any) => <span style={{ color: '#c0264a', fontWeight: 700 }}>{formatMoney(p.cost_eur, locale)}</span> },
                                { key: 'lines', label: locale === 'en' ? 'Lines' : 'Redaka', align: 'right', render: (p: any) => formatNumber(p.lines, locale) },
                            ] as Col<any>[]}
                            rows={payload.intercompany.by_company_pair.map((p, i) => ({ ...p, id: i }))} />
                    </UiCard>
                ) : null}
            </div>
        );
    }

    return (
        <section style={{ display: 'grid', gap: '1rem' }}>
            {/* TOOLBAR — IC mode + Comparison toggle */}
            <div className="card" style={{ padding: '0.75rem 1rem', display: 'flex', flexWrap: 'wrap', gap: '1.25rem', alignItems: 'center' }}>
                <div>
                    <label className="bi-seg-label" style={{ display: 'block', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.35rem', color: '#9c8a55' }}>
                        Intercompany
                    </label>
                    <div className="bi-seg" role="tablist">
                        {(['include', 'eliminate'] as const).map((m) => (
                            <button
                                key={m}
                                type="button"
                                className={'bi-seg-btn' + (intercompanyMode === m ? ' is-active' : '')}
                                onClick={() => onIntercompanyModeChange(m)}
                            >
                                {m === 'include' ? (locale === 'en' ? 'Per-company' : 'Per-firma') : (locale === 'en' ? 'Group consolidated' : 'Konsolidirano')}
                            </button>
                        ))}
                    </div>
                </div>
                <div>
                    <label className="bi-seg-label" style={{ display: 'block', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.35rem', color: '#9c8a55' }}>
                        {locale === 'en' ? 'Compare to' : 'Usporedi s'}
                    </label>
                    <div className="bi-seg" role="tablist">
                        {(['none', 'mom', 'qoq', 'yoy', 'custom'] as const).map((m) => (
                            <button
                                key={m}
                                type="button"
                                className={'bi-seg-btn' + (comparisonMode === m ? ' is-active' : '')}
                                onClick={() => onComparisonModeChange(m)}
                            >
                                {m === 'none' ? '—' : m === 'mom' ? 'MoM' : m === 'qoq' ? 'QoQ' : m === 'yoy' ? 'YoY' : (locale === 'en' ? 'Custom' : 'Vlastito')}
                            </button>
                        ))}
                    </div>
                </div>
                {(comparisonMode === 'mom' || comparisonMode === 'qoq' || comparisonMode === 'yoy') && explicitCompare && onExplicitCompareChange ? (() => {
                    const opts = periodOptions(comparisonMode, new Date().toISOString().slice(0, 10), comparisonMode === 'yoy' ? 10 : 24);
                    const curRange = periodRange(comparisonMode, explicitCompare.current_key);
                    const cmpRange = periodRange(comparisonMode, explicitCompare.compare_key);
                    const periodLabel = comparisonMode === 'mom' ? (locale === 'en' ? 'Month' : 'Mjesec') : comparisonMode === 'qoq' ? (locale === 'en' ? 'Quarter' : 'Kvartal') : (locale === 'en' ? 'Year' : 'Godina');

                    return (
                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.35rem', color: '#9c8a55' }}>
                                    {locale === 'en' ? `Current ${periodLabel}` : `Trenutni ${periodLabel}`}
                                </label>
                                <select
                                    value={explicitCompare.current_key}
                                    onChange={(e) => onExplicitCompareChange({ ...explicitCompare, current_key: e.target.value })}
                                    style={{ padding: '0.35rem 0.5rem', minWidth: 120 }}
                                >
                                    {opts.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                                </select>
                                <div style={{ fontSize: '0.7rem', color: '#9c8a5599', marginTop: '0.2rem' }}>{curRange.from} → {curRange.to}</div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', fontSize: '1.4rem', color: '#cfb88c', alignSelf: 'flex-end', paddingBottom: '0.85rem' }}>vs</div>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.35rem', color: '#9c8a55' }}>
                                    {locale === 'en' ? `Compare ${periodLabel}` : `Usporedi ${periodLabel}`}
                                </label>
                                <select
                                    value={explicitCompare.compare_key}
                                    onChange={(e) => onExplicitCompareChange({ ...explicitCompare, compare_key: e.target.value })}
                                    style={{ padding: '0.35rem 0.5rem', minWidth: 120 }}
                                >
                                    {opts.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                                </select>
                                <div style={{ fontSize: '0.7rem', color: '#9c8a5599', marginTop: '0.2rem' }}>{cmpRange.from} → {cmpRange.to}</div>
                            </div>
                        </div>
                    );
                })() : null}
                {comparisonMode === 'custom' && customCompare && onCustomCompareChange ? (
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
                        <label style={{ fontSize: '0.78rem', color: '#9c8a55' }}>
                            {locale === 'en' ? 'Compare from' : 'Usporedba od'}
                            <DateInput
                                value={customCompare.date_from}
                                onChange={(iso) => onCustomCompareChange({ ...customCompare, date_from: iso })}
                                style={{ display: 'block', marginTop: '0.2rem' }}
                            />
                        </label>
                        <label style={{ fontSize: '0.78rem', color: '#9c8a55' }}>
                            {locale === 'en' ? 'Compare to' : 'Usporedba do'}
                            <DateInput
                                value={customCompare.date_to}
                                onChange={(iso) => onCustomCompareChange({ ...customCompare, date_to: iso })}
                                style={{ display: 'block', marginTop: '0.2rem' }}
                            />
                        </label>
                    </div>
                ) : null}
                {toolbarExtra}
            </div>

            {/* KPI CARDS */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '0.85rem' }}>
                {kpiCards.map((k) => (
                    <div
                        key={k.label}
                        className="card"
                        style={{
                            padding: '0.9rem 1rem',
                            background: k.hero ? `linear-gradient(135deg, ${k.color}33, transparent)` : undefined,
                            borderLeft: `4px solid ${k.color}`,
                        }}
                    >
                        <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9c8a55', marginBottom: '0.35rem' }}>{k.label}</div>
                        <div style={{ fontSize: k.hero ? '1.6rem' : '1.25rem', fontWeight: 700, color: k.value >= 0 ? '#e7d8a7' : '#ef9a9a' }}>
                            {formatMoney(k.value, locale)}
                        </div>
                        {k.pct !== undefined ? (
                            <div style={{ fontSize: '0.78rem', color: '#cfb88c', marginTop: '0.18rem' }}>
                                {formatNumber(k.pct, locale)}%
                            </div>
                        ) : null}
                        {k.delta ? (
                            <div style={{ fontSize: '0.78rem', marginTop: '0.32rem', color: k.delta.pct >= 0 ? '#a5d6a7' : '#ef9a9a' }}>
                                {k.delta.pct >= 0 ? '▲' : '▼'} {formatNumber(Math.abs(k.delta.pct), locale)}% ({k.delta.abs >= 0 ? '+' : ''}{formatMoney(k.delta.abs, locale)})
                            </div>
                        ) : null}
                    </div>
                ))}
            </div>
            {belowKpi}

            {/* RATIO KPI GRID — financijski pokazatelji + bullet gauge */}
            {payload.ratios ? (
                <div className="card" style={{ padding: '1rem' }}>
                    <h3 style={{ margin: '0 0 0.65rem 0' }}>
                        {locale === 'en' ? 'Financial ratios' : 'Financijski pokazatelji'}
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.65rem', marginBottom: 14 }}>
                        {Object.entries(payload.ratios).map(([key, r]) => {
                            const cmpR = comparePayload?.ratios?.[key];
                            const delta = cmpR ? r.value - cmpR.value : null;
                            const label = locale === 'en' ? r.label_en : r.label;
                            const isMoney = r.fmt === 'money';
                            const color = key.includes('margin') || key.includes('intensity') ? (r.value >= 0 ? '#a5d6a7' : '#ef9a9a') : '#cfb88c';
                            return (
                                <div key={key} className="card" style={{ padding: '0.6rem 0.75rem', background: 'rgba(255,255,255,0.02)', borderLeft: '3px solid #cfb88c44' }}>
                                    <div style={{ fontSize: '0.7rem', color: '#9c8a55', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.2rem' }}>{label}</div>
                                    <div style={{ fontSize: '1.1rem', fontWeight: 700, color }}>
                                        {isMoney ? formatMoney(r.value, locale) : formatNumber(r.value, locale) + '%'}
                                    </div>
                                    {delta !== null ? (
                                        <div style={{ fontSize: '0.72rem', marginTop: '0.18rem', color: delta >= 0 ? '#a5d6a7' : '#ef9a9a' }}>
                                            {delta >= 0 ? '▲' : '▼'} {isMoney ? formatMoney(Math.abs(delta), locale) : formatNumber(Math.abs(delta), locale) + 'pp'}
                                        </div>
                                    ) : null}
                                    <div style={{ fontSize: '0.66rem', color: '#9c8a5599', marginTop: '0.25rem' }}>{r.hint}</div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Bullet gauges za postotak-ratio-e (vs prior period kao target) */}
                    {(() => {
                        const pctRatios = Object.entries(payload.ratios)
                            .filter(([, r]) => r.fmt === 'pct')
                            .slice(0, 8);
                        if (pctRatios.length === 0) return null;
                        return (
                            <div>
                                <p className="muted" style={{ fontSize: '0.72rem', margin: '0 0 6px', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#9c8a55' }}>
                                    {locale === 'en' ? 'Bullet view (actual ↔ prior)' : 'Bullet pogled (sada ↔ prošli period)'}
                                </p>
                                <div style={{ display: 'grid', gap: 6 }}>
                                    {pctRatios.map(([key, r]) => {
                                        const cmp = comparePayload?.ratios?.[key];
                                        const target = cmp?.value;
                                        const candidates = [Math.abs(r.value), Math.abs(target ?? 0), 10];
                                        const max = Math.max(Math.max(...candidates), Math.abs(r.value) * 1.4);
                                        const label = locale === 'en' ? r.label_en : r.label;
                                        return (
                                            <BulletChart
                                                key={key}
                                                label={label}
                                                actual={r.value}
                                                target={target}
                                                max={max}
                                                locale={locale}
                                                valueFormatter={(n) => formatNumber(n, locale) + '%'}
                                                zones={[
                                                    { until: max * 0.33, color: '#fce4e4' },
                                                    { until: max * 0.66, color: '#fff4d6' },
                                                    { until: max, color: '#e3f5e8' },
                                                ]}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })()}
                </div>
            ) : null}

            {/* NORMALIZED COMPARISON BAR CHART (index 100 = base) */}
            {comparePayload && payload ? (
                <div className="card" style={{ padding: '1rem' }}>
                    <h3 style={{ margin: '0 0 0.65rem 0' }}>
                        {locale === 'en' ? 'Normalized comparison (base = 100)' : 'Normalizirana usporedba (baza = 100)'}
                    </h3>
                    <NormalizedComparisonChart current={payload} compare={comparePayload} locale={locale} />
                </div>
            ) : null}

            {/* MONTHLY STACKED BAR CHART */}
            {monthlyChart && monthlyChart.months.length > 0 ? (
                <div className="card" style={{ padding: '1rem' }}>
                    <h3 style={{ margin: '0 0 0.65rem 0' }}>{locale === 'en' ? 'Monthly P&L composition' : 'Mjesečni sastav P&L'}</h3>
                    <StackedBarChart series={monthlyChart.series} months={monthlyChart.months} locale={locale} />
                    <Legend buckets={monthlyChart.series.map((s) => ({ bucket: s.bucket, color: s.color }))} locale={locale} />
                </div>
            ) : null}

            {/* BUCKET DETAIL TABLE + DRILLDOWN (inside same card) */}
            <div className="card" style={{ padding: '1rem' }}>
                <h3 style={{ margin: '0 0 0.75rem 0' }}>{locale === 'en' ? 'Buckets' : 'Kategorije'}</h3>
                <table className="bi-table">
                    <thead>
                        <tr>
                            <th>{locale === 'en' ? 'Bucket' : 'Kategorija'}</th>
                            <th style={{ textAlign: 'right' }}>{locale === 'en' ? 'Total' : 'Ukupno'}</th>
                            <th style={{ textAlign: 'right' }}>{locale === 'en' ? 'Lines' : 'Redaka'}</th>
                            <th style={{ textAlign: 'right' }}>%</th>
                        </tr>
                    </thead>
                    <tbody>
                        {payload.buckets.map((b) => {
                            const pctOfRev = payload.kpi.revenue_eur > 0 ? (b.total_eur / payload.kpi.revenue_eur) * 100 : 0;
                            const isOpen = drilldown?.lvl1 === b.bucket_lvl1;
                            return (
                                <tr
                                    key={b.bucket_lvl1}
                                    style={{ borderLeft: `4px solid ${BUCKET_COLORS[b.bucket_lvl1] ?? '#999'}`, cursor: 'pointer', background: isOpen ? 'rgba(207,184,140,0.08)' : undefined }}
                                    onClick={() => isOpen ? setDrilldown(null) : openDrilldown(b.bucket_lvl1)}
                                    title={locale === 'en' ? 'Click to drill down' : 'Klikni za drilldown'}
                                >
                                    <td><strong>{isOpen ? '▾' : '▸'} {translateBucket(b.bucket_lvl1, locale)}</strong></td>
                                    <td style={{ textAlign: 'right', color: b.total_eur >= 0 ? '#a5d6a7' : '#ef9a9a' }}>{formatMoney(b.total_eur, locale)}</td>
                                    <td style={{ textAlign: 'right' }}>{formatNumber(b.count, locale)}</td>
                                    <td style={{ textAlign: 'right' }}>{formatNumber(pctOfRev, locale)}%</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>

                {/* INLINE DRILLDOWN — radi unutar kategorije card-a */}
                {drilldown ? (
                    <div style={{ marginTop: '1rem', padding: '0.85rem 1rem', borderLeft: '4px solid #cfb88c', background: 'rgba(207,184,140,0.04)', borderRadius: 4 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.65rem' }}>
                            <h4 style={{ margin: 0, color: '#cfb88c' }}>
                                🔍 Drilldown: <strong>{translateBucket(drilldown.lvl1, locale)}</strong>
                                {drilldown.lvl2 ? <span style={{ color: '#9c8a55' }}> / {translateBucket(drilldown.lvl2, locale)}</span> : null}
                                {drilldown.lvl3 ? <span style={{ color: '#9c8a55' }}> / {drilldown.lvl3}</span> : null}
                            </h4>
                            <button type="button" className="bi-seg-btn" onClick={() => setDrilldown(null)}>✕ {locale === 'en' ? 'Close' : 'Zatvori'}</button>
                        </div>
                        {drilldownLoading ? <BrandSpinner size="sm" label={locale === 'en' ? 'Loading…' : 'Učitavam…'} /> : null}

                        {!drilldown.lvl2 ? (
                            <div>
                                <h5 style={{ margin: '0.5rem 0', color: '#cfb88c' }}>
                                    {locale === 'en' ? 'Sub-categories (Mapiranje LVL2)' : 'Sub-kategorije (Mapiranje LVL2)'}
                                </h5>
                                {drilldown.sub_buckets.length > 0 ? (
                                    <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 6, padding: '0.6rem 0.85rem', marginBottom: 12 }}>
                                        <RankedBar
                                            locale={locale}
                                            maxBars={10}
                                            items={drilldown.sub_buckets.map((s: any) => ({
                                                label: translateBucket(s.bucket_lvl2, locale),
                                                value: s.total_eur,
                                                sub: `${formatNumber(s.count, locale)} ${locale === 'en' ? 'lines' : 'redaka'}`,
                                            }))}
                                            moneyFormatter={(n) => formatMoney(n, locale)}
                                        />
                                    </div>
                                ) : null}
                                <table className="bi-table">
                                    <thead>
                                        <tr>
                                            <th>{locale === 'en' ? 'Sub-category' : 'Sub-kategorija'}</th>
                                            <th style={{ textAlign: 'right' }}>{locale === 'en' ? 'Total' : 'Ukupno'}</th>
                                            <th style={{ textAlign: 'right' }}>{locale === 'en' ? 'Lines' : 'Redaka'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {drilldown.sub_buckets.map((s, i) => (
                                            <tr
                                                key={i}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => openDrilldown(drilldown.lvl1, s.bucket_lvl2)}
                                                title={locale === 'en' ? 'Click for suppliers' : 'Klikni za dobavljače'}
                                            >
                                                <td>▸ <strong>{translateBucket(s.bucket_lvl2, locale)}</strong></td>
                                                <td style={{ textAlign: 'right', color: s.total_eur >= 0 ? '#a5d6a7' : '#ef9a9a' }}>{formatMoney(s.total_eur, locale)}</td>
                                                <td style={{ textAlign: 'right' }}>{formatNumber(s.count, locale)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : !drilldown.lvl3 ? (
                            <div>
                                <button type="button" className="bi-seg-btn" style={{ marginBottom: '0.5rem' }} onClick={() => openDrilldown(drilldown.lvl1)}>
                                    ← {locale === 'en' ? 'Back to sub-categories' : 'Natrag na sub-kategorije'}
                                </button>
                                <h5 style={{ margin: '0.5rem 0', color: '#cfb88c' }}>
                                    {locale === 'en' ? 'Top suppliers / partners' : 'Top dobavljači / partneri'} <span style={{ color: '#9c8a55', fontWeight: 400 }}>({translateBucket(drilldown.lvl2, locale)})</span>
                                </h5>
                                <table className="bi-table">
                                    <thead>
                                        <tr>
                                            <th>{locale === 'en' ? 'Partner / Subject' : 'Partner / Subjekt'}</th>
                                            <th style={{ textAlign: 'right' }}>{locale === 'en' ? 'Total' : 'Ukupno'}</th>
                                            <th style={{ textAlign: 'right' }}>{locale === 'en' ? 'Lines' : 'Redaka'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {drilldown.suppliers.map((s, i) => (
                                            <tr
                                                key={i}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => openDrilldown(drilldown.lvl1, drilldown.lvl2, s.bucket_lvl3)}
                                                title={locale === 'en' ? 'Click for invoices' : 'Klikni za fakture'}
                                            >
                                                <td>▸ <strong>{s.bucket_lvl3 || s.partner_subjekt}</strong></td>
                                                <td style={{ textAlign: 'right', color: s.total_eur >= 0 ? '#a5d6a7' : '#ef9a9a' }}>{formatMoney(s.total_eur, locale)}</td>
                                                <td style={{ textAlign: 'right' }}>{formatNumber(s.count, locale)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div>
                                <button type="button" className="bi-seg-btn" style={{ marginBottom: '0.5rem' }} onClick={() => openDrilldown(drilldown.lvl1, drilldown.lvl2)}>
                                    ← {locale === 'en' ? 'Back to suppliers' : 'Natrag na dobavljače'}
                                </button>
                                <h5 style={{ margin: '0.5rem 0', color: '#cfb88c' }}>
                                    {locale === 'en' ? 'Top invoices / documents' : 'Top fakture / dokumenti'} <span style={{ color: '#9c8a55', fontWeight: 400 }}>({drilldown.lvl3})</span>
                                </h5>
                                <table className="bi-table">
                                    <thead>
                                        <tr>
                                            <th>{locale === 'en' ? 'Document' : 'Dokument'}</th>
                                            <th>{locale === 'en' ? 'Date' : 'Datum'}</th>
                                            <th>Konto</th>
                                            <th>{locale === 'en' ? 'Account name' : 'Naziv konta'}</th>
                                            <th style={{ textAlign: 'right' }}>{locale === 'en' ? 'Amount' : 'Iznos'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {drilldown.invoices.map((inv, i) => (
                                            <tr key={i}>
                                                <td>{inv.document}</td>
                                                <td>{inv.datum}</td>
                                                <td>{inv.konto}</td>
                                                <td style={{ fontSize: '0.78rem', color: '#cfb88c' }}>{inv.naziv_konta}</td>
                                                <td style={{ textAlign: 'right', color: inv.amount_eur >= 0 ? '#a5d6a7' : '#ef9a9a' }}>{formatMoney(inv.amount_eur, locale)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                ) : null}
            </div>

            {/* TARGETS AUDIT — facts vs GFI Tekuća per firma */}
            {payload.targets_audit && payload.targets_audit.length > 0 ? (
                <div className="card" style={{ padding: '1rem' }}>
                    <h3 style={{ margin: '0 0 0.65rem 0' }}>
                        {locale === 'en' ? 'Targets audit (facts vs GFI Tekuća)' : 'Targets audit (facts vs GFI Tekuća)'}
                    </h3>
                    <table className="bi-table">
                        <thead>
                            <tr>
                                <th>{locale === 'en' ? 'Company' : 'Firma'}</th>
                                <th style={{ textAlign: 'right' }}>Net facts</th>
                                <th style={{ textAlign: 'right' }}>Net GFI</th>
                                <th style={{ textAlign: 'right' }}>Δ EUR</th>
                                <th style={{ textAlign: 'right' }}>Δ %</th>
                                <th style={{ textAlign: 'right' }}>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {payload.targets_audit.map((t) => {
                                const status = t.net_target === null ? '—' : (Math.abs(t.net_delta ?? 0) < 100 ? '✓' : (Math.abs(t.net_delta_pct ?? 0) < 1 ? '⚠ <1%' : '❌ >1%'));
                                const color = status === '✓' ? '#a5d6a7' : (status.startsWith('⚠') ? '#ffe082' : (status.startsWith('❌') ? '#ef9a9a' : '#cfb88c'));
                                return (
                                    <tr key={t.company_code}>
                                        <td><strong>{t.company_code}</strong></td>
                                        <td style={{ textAlign: 'right' }}>{formatMoney(t.net_facts, locale)}</td>
                                        <td style={{ textAlign: 'right' }}>{t.net_target !== null ? formatMoney(t.net_target, locale) : '—'}</td>
                                        <td style={{ textAlign: 'right', color }}>{t.net_delta !== null ? formatMoney(t.net_delta, locale) : '—'}</td>
                                        <td style={{ textAlign: 'right', color }}>{t.net_delta_pct !== null ? formatNumber(t.net_delta_pct, locale) + '%' : '—'}</td>
                                        <td style={{ textAlign: 'right', color }}>{status}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    <p style={{ marginTop: '0.5rem', fontSize: '0.78rem', color: '#9c8a55' }}>
                        {locale === 'en' ? 'Targets sourced from GFI POD (AOP 186 Tekuća). Edit in `bi_pnl_targets`.' : 'Targets iz GFI POD (AOP 186 Tekuća). Edit u tablici `bi_pnl_targets`.'}
                    </p>
                </div>
            ) : null}

            {/* SUB-BUCKETS DETAIL (bucket_lvl2 razlaganje) */}
            {payload.sub_buckets && payload.sub_buckets.length > 0 ? (
                <div className="card" style={{ padding: '1rem' }}>
                    <h3 style={{ margin: '0 0 0.65rem 0' }}>
                        {locale === 'en' ? 'Sub-bucket detail' : 'Detalj po sub-kategoriji'}
                    </h3>
                    <table className="bi-table">
                        <thead>
                            <tr>
                                <th>{locale === 'en' ? 'Bucket' : 'Kategorija'}</th>
                                <th>{locale === 'en' ? 'Sub-bucket' : 'Sub-kategorija'}</th>
                                <th style={{ textAlign: 'right' }}>{locale === 'en' ? 'Total' : 'Ukupno'}</th>
                                <th style={{ textAlign: 'right' }}>%</th>
                                <th style={{ textAlign: 'right' }}>{locale === 'en' ? 'Lines' : 'Redaka'}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {payload.sub_buckets.slice(0, 30).map((s, i) => {
                                const pct = payload.kpi.revenue_eur > 0 ? (s.total_eur / payload.kpi.revenue_eur) * 100 : 0;
                                return (
                                    <tr
                                        key={i}
                                        style={{ borderLeft: `3px solid ${BUCKET_COLORS[s.bucket_lvl1] ?? '#999'}`, cursor: 'pointer' }}
                                        onClick={() => openDrilldown(s.bucket_lvl1, s.bucket_lvl2)}
                                        title={locale === 'en' ? 'Click to drill down' : 'Klikni za drilldown'}
                                    >
                                        <td>{translateBucket(s.bucket_lvl1, locale)}</td>
                                        <td>▸ {translateBucket(s.bucket_lvl2, locale)}</td>
                                        <td style={{ textAlign: 'right', color: s.total_eur >= 0 ? '#a5d6a7' : '#ef9a9a' }}>{formatMoney(s.total_eur, locale)}</td>
                                        <td style={{ textAlign: 'right' }}>{formatNumber(pct, locale)}%</td>
                                        <td style={{ textAlign: 'right' }}>{formatNumber(s.count, locale)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            ) : null}

            {/* INTERCOMPANY AUDIT */}
            {payload.intercompany && payload.intercompany.by_company_pair.length > 0 ? (
                <div className="card" style={{ padding: '1rem' }}>
                    <h3 style={{ margin: '0 0 0.65rem 0' }}>
                        {locale === 'en' ? 'Intercompany pairs' : 'Intercompany parovi'} ({payload.intercompany.mode})
                    </h3>
                    <table className="bi-table">
                        <thead>
                            <tr>
                                <th>{locale === 'en' ? 'From' : 'Od'}</th>
                                <th>{locale === 'en' ? 'To' : 'Prema'}</th>
                                <th style={{ textAlign: 'right' }}>{locale === 'en' ? 'Revenue' : 'Prihod'}</th>
                                <th style={{ textAlign: 'right' }}>{locale === 'en' ? 'Cost' : 'Trošak'}</th>
                                <th style={{ textAlign: 'right' }}>{locale === 'en' ? 'Lines' : 'Redaka'}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {payload.intercompany.by_company_pair.map((p, i) => (
                                <tr key={i}>
                                    <td>{p.from_code}</td>
                                    <td>{p.to_code}</td>
                                    <td style={{ textAlign: 'right', color: '#a5d6a7' }}>{formatMoney(p.revenue_eur, locale)}</td>
                                    <td style={{ textAlign: 'right', color: '#ef9a9a' }}>{formatMoney(p.cost_eur, locale)}</td>
                                    <td style={{ textAlign: 'right' }}>{formatNumber(p.lines, locale)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : null}
        </section>
    );
}

function StackedBarChart({
    series,
    months,
    locale = 'hr',
}: {
    series: Array<{ bucket: string; color: string; values: number[]; kind: string }>;
    months: string[];
    locale?: 'hr' | 'en';
}) {
    const width = 1100;
    const height = 320;
    const padding = { top: 20, right: 40, bottom: 50, left: 80 };
    const plotW = width - padding.left - padding.right;
    const plotH = height - padding.top - padding.bottom;

    // For each month, calculate positive stack height + negative stack height
    const monthlyMaxPos = months.map((_, mi) => series.reduce((sum, s) => sum + Math.max(0, s.values[mi]), 0));
    const monthlyMinNeg = months.map((_, mi) => series.reduce((sum, s) => sum + Math.min(0, s.values[mi]), 0));
    const yMax = Math.max(...monthlyMaxPos, 1);
    const yMin = Math.min(...monthlyMinNeg, 0);

    const yScale = (v: number) => padding.top + plotH - ((v - yMin) / (yMax - yMin)) * plotH;
    const xScale = (i: number) => padding.left + (i + 0.5) * (plotW / months.length);
    const barW = (plotW / months.length) * 0.75;

    return (
        <svg width="100%" viewBox={`0 0 ${width} ${height}`} style={{ background: 'transparent' }}>
            {/* Y axis grid */}
            {[0, 0.25, 0.5, 0.75, 1].map((p) => {
                const v = yMin + p * (yMax - yMin);
                const y = yScale(v);

                return (
                    <g key={p}>
                        <line x1={padding.left} y1={y} x2={padding.left + plotW} y2={y} stroke="rgba(200,180,130,0.12)" />
                        <text x={padding.left - 8} y={y + 4} fontSize="11" fill="#9c8a55" textAnchor="end">
                            {formatMoneyAxis(v, locale)}
                        </text>
                    </g>
                );
            })}

            {/* Zero line */}
            <line x1={padding.left} y1={yScale(0)} x2={padding.left + plotW} y2={yScale(0)} stroke="rgba(200,180,130,0.4)" />

            {/* Bars per month */}
            {months.map((month, mi) => {
                const cx = xScale(mi);
                const x = cx - barW / 2;
                let yPos = yScale(0);
                let yNeg = yScale(0);
                const segments: React.ReactElement[] = [];
                series.forEach((s, si) => {
                    const v = s.values[mi];
                    if (Math.abs(v) < 0.01) return;
                    const h = Math.abs(yScale(v) - yScale(0));
                    if (v > 0) {
                        yPos -= h;
                        segments.push(<rect key={si} x={x} y={yPos} width={barW} height={h} fill={s.color} opacity={0.85}><title>{translateBucket(s.bucket, 'hr')}: {formatMoney(v, locale)}</title></rect>);
                    } else {
                        segments.push(<rect key={si} x={x} y={yNeg} width={barW} height={h} fill={s.color} opacity={0.85}><title>{s.bucket}: {formatMoney(v, locale)}</title></rect>);
                        yNeg += h;
                    }
                });

                return (
                    <g key={month}>
                        {segments}
                        <text x={cx} y={padding.top + plotH + 18} fontSize="11" fill="#cfb88c" textAnchor="middle">{month.slice(2)}</text>
                    </g>
                );
            })}
        </svg>
    );
}

/**
 * Normalizirani komparativni bar chart (base = 100).
 * Layout: | Label | Bar viewport | Value | Δ% |
 */
function NormalizedComparisonChart({
    current,
    compare,
    locale = 'hr',
}: {
    current: PnlV2Payload;
    compare: PnlV2Payload;
    locale?: 'hr' | 'en';
}) {
    const metrics: Array<{ key: string; label: string; getValue: (p: PnlV2Payload) => number }> = [
        { key: 'revenue', label: locale === 'en' ? 'Revenue' : 'Prihodi', getValue: (p) => p.kpi.revenue_eur },
        { key: 'cogs', label: 'COGS', getValue: (p) => Math.abs(p.kpi.cogs_eur) },
        { key: 'gross', label: locale === 'en' ? 'Gross margin' : 'Bruto marža', getValue: (p) => p.kpi.revenue_eur + p.kpi.cogs_eur },
        { key: 'opex', label: 'OPEX', getValue: (p) => Math.abs(p.kpi.opex_eur) },
        { key: 'net', label: locale === 'en' ? 'Net result' : 'Neto rezultat', getValue: (p) => p.kpi.net_result_eur },
    ];

    const width = 1280;
    const rowH = 56;
    const padding = { top: 30, right: 20, bottom: 24, left: 170 };
    // Kolonе:  bar viewport | value col | delta col
    const deltaColW = 120;
    const valueColW = 180;
    const barViewW = width - padding.left - padding.right - valueColW - deltaColW;
    const height = padding.top + padding.bottom + metrics.length * rowH + 30;

    const barX = padding.left;
    const valueX = barX + barViewW + 16;
    const deltaX = valueX + valueColW;

    return (
        <svg width="100%" viewBox={`0 0 ${width} ${height}`}>
            {/* Header */}
            <text x={padding.left} y={18} fontSize="11" fill="#9c8a55">●</text>
            <text x={padding.left + 14} y={18} fontSize="11" fill="#9c8a55">{locale === 'en' ? 'Current' : 'Trenutni'}</text>
            <text x={padding.left + 90} y={18} fontSize="11" fill="#9c8a55">●</text>
            <text x={padding.left + 104} y={18} fontSize="11" fill="#9c8a55" opacity={0.6}>{locale === 'en' ? 'Compare' : 'Usporedba'}</text>

            {metrics.map((m, i) => {
                const cur = m.getValue(current);
                const cmp = m.getValue(compare);
                const baseAbs = Math.max(Math.abs(cur), Math.abs(cmp), 1);
                const y = padding.top + i * rowH;
                const baseY = y + 8;
                const compY = y + 28;
                const barH = 16;
                const curW = (Math.abs(cur) / baseAbs) * barViewW;
                const cmpW = (Math.abs(cmp) / baseAbs) * barViewW;
                const curColor = cur >= 0 ? '#4caf50' : '#e57373';
                const cmpColor = cmp >= 0 ? '#26a69a' : '#d32f2f';
                const deltaPct = cmp !== 0 ? ((cur - cmp) / Math.abs(cmp)) * 100 : 0;
                const deltaColor = deltaPct >= 0 ? '#a5d6a7' : '#ef9a9a';

                return (
                    <g key={m.key}>
                        {/* Row separator */}
                        {i > 0 ? <line x1={padding.left} y1={y - 2} x2={width - padding.right} y2={y - 2} stroke="rgba(207,184,140,0.08)" /> : null}

                        {/* Label */}
                        <text x={padding.left - 10} y={y + rowH * 0.5} fontSize="13" fontWeight="600" fill="#cfb88c" textAnchor="end" dominantBaseline="middle">{m.label}</text>

                        {/* Current bar */}
                        <rect x={barX} y={baseY} width={curW} height={barH} fill={curColor} opacity={0.95} rx={2}>
                            <title>{locale === 'en' ? 'Current' : 'Trenutni'}: {formatMoney(cur, locale)}</title>
                        </rect>

                        {/* Compare bar (manji + opacity) */}
                        <rect x={barX} y={compY} width={cmpW} height={barH * 0.75} fill={cmpColor} opacity={0.45} rx={2}>
                            <title>{locale === 'en' ? 'Compare' : 'Usporedba'}: {formatMoney(cmp, locale)}</title>
                        </rect>

                        {/* Value column — current bold, compare manji */}
                        <text x={valueX + valueColW - 8} y={baseY + barH - 2} fontSize="13" fontWeight="700" fill={curColor} textAnchor="end">{formatMoney(cur, locale)}</text>
                        <text x={valueX + valueColW - 8} y={compY + barH * 0.75 - 1} fontSize="11" fill="#9c8a55" textAnchor="end">{formatMoney(cmp, locale)}</text>

                        {/* Δ% column */}
                        <text x={deltaX + deltaColW - 8} y={y + rowH * 0.5 + 5} fontSize="17" fontWeight="800" fill={deltaColor} textAnchor="end">
                            {deltaPct >= 0 ? '▲' : '▼'} {formatNumber(Math.abs(deltaPct), locale)}%
                        </text>
                    </g>
                );
            })}
        </svg>
    );
}

function Legend({ buckets, locale = 'hr' }: { buckets: Array<{ bucket: string; color: string }>; locale?: 'hr' | 'en' }) {
    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginTop: '0.5rem' }}>
            {buckets.map((b) => (
                <span key={b.bucket} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.82rem' }}>
                    <span style={{ width: 12, height: 12, background: b.color, display: 'inline-block', borderRadius: 2 }} />
                    {translateBucket(b.bucket, locale)}
                </span>
            ))}
        </div>
    );
}
